import { Component, OnInit, Input, Inject, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToolCategory, ToolSubcategory, ToolParameterType, ToolDeliverableType } from 'app/profile/models/generic.model';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { SelectToolCategories, SelectToolSubCategories } from 'app/profile/store/app.selector';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';
import { Store } from '@ngrx/store';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatChipInputEvent} from '@angular/material/chips';
import { PageEvent} from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TslSelectChange, TslDialog } from '@tesla/ng-tds';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ProfileService } from 'app/profile/services/profile.service';
import { I18nService } from 'app/shared/services/i18n.service';

@Component({
  selector: 'app-manage-defaults',
  templateUrl: './manage-defaults.component.html',
  styleUrls: ['./manage-defaults.component.scss']
})
export class ManageDefaultsComponent implements OnInit {
  manageGroup: FormGroup;
  supplierCode: string;
  isOwner: boolean;

  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  displayedProposedColumns = ['subcategory', 'type', 'eventType', 'description'];
  pIndex = 0;
  pSize = 10;
  length = 0;

  filterCategories: ToolCategory[];
  filterSubcategories: ToolSubcategory[];
  allSubcategories: ToolSubcategory[];
  defaultTypes: string[];
  eventTypes: string[];

  parameterList: (ToolParameterType | any)[] = [];
  searchParameterList: (ToolParameterType | any)[] = [];
  defParameterList: (ToolParameterType | any)[] = [];
  searchDeliverableList: (ToolParameterType | any)[] = [];
  proposedParamsList: (ToolParameterType | any)[] = [];
  isParam: boolean = false;
  isDeliverable: boolean = false;
  deliverableList: (ToolDeliverableType | any)[] = [];
  defDeliverableList: (ToolDeliverableType | any)[] = [];
  proposedDeliverableList: (ToolDeliverableType | any)[] = [];

  dataSource: BehaviorSubject<any> = new BehaviorSubject([]);
  defaultDataSource: BehaviorSubject<any> = new BehaviorSubject([]);
  displayedDefaultsColumns = ['subcategory', 'type', 'eventType', 'description', 'delete'];
  pageSizeOptions: number[] = [10, 25, 50, 100, 250];
  pageIndex = 0;
  pageSize = 10;
  total = 0;
  deafultsData: any[];
  proposedDefaults: any[];

  get toolCategory() { return this.manageGroup.get('toolCategory'); }
  get toolSubcategory() { return this.manageGroup.get('toolSubcategory'); }
  get defaultType() { return this.manageGroup.get('defaultType') }
  get eventType() { return this.manageGroup.get('eventType') }
  get parameter() { return this.manageGroup.get('parameter') }
  get deliverable() { return this.manageGroup.get('deliverable') }

  constructor(private fb: FormBuilder,
    public dialogRef: MatDialogRef<ManageDefaultsComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private store: Store<GeneralSettingsState>,
    private service: ProfileService,
    private snackBar: MatSnackBar,
    private dialog: TslDialog,
    public translate: I18nService,) {
    this.manageGroup = this.fb.group({
      toolCategory: ['', Validators.required],
      toolSubcategory: [[], Validators.required],
      defaultType: [{ value: null, disabled: true }, Validators.required],
      parameter: ['', [Validators.pattern("^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$")]],
      eventType: [''],
      deliverable: ['', [Validators.pattern("^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$")]]
    });
    this.defaultTypes = ['Characteristic', 'Deliverable'];
    this.eventTypes = ['Design', 'Build', 'Commission', 'Maintenance'];
  }

  ngOnInit() {
    this.manageGroup.get('parameter').valueChanges
      .debounceTime(400).subscribe(val => {
        if (val) {
          this.getParametersList(val.trim());
        }
      });
    this.manageGroup.get('deliverable').valueChanges
      .debounceTime(400).subscribe(val => {
        if (val) {
          this.getDeliverablesList(val.trim());
        }
      });
    this.loadResources();
  }

  public loadResources() {
    this.service.getManageDefaultsPermissions().subscribe(permission => {
      this.isOwner = permission;
    });
    this.service.getDefaultParameters()
      .subscribe(res => {
        this.defParameterList.push(...res);
      });
    this.service.getPendingDefaultParameters().subscribe(res => {
      if (res && res.length > 0) {
        this.proposedParamsList.push(...res);
      }
    });
    this.service.getDefaultDeliverables()
      .subscribe(res => {
        this.defDeliverableList.push(...res);
      });
    this.service.getPendingDefaultDeliverables().subscribe(res => {
      if (res && res.length > 0) {
        this.proposedDeliverableList.push(...res);
      }
    });
    combineLatest(
      [this.store.select(SelectToolCategories),
      this.store.select(SelectToolSubCategories)])
      .subscribe(([categories, subCategories]) => {
        if (categories && subCategories) {
          this.filterCategories = categories.sort((a, b) => a.category.trim().toLowerCase().localeCompare(b.category.trim().toLowerCase(), undefined, { sensitivity: 'base' }));;
          this.allSubcategories = subCategories[this.dialogData.supplierCode].sort((a, b) => a.subcategory.trim().toLowerCase().localeCompare(b.subcategory.trim().toLowerCase(), undefined, { sensitivity: 'base' }));
        }
      });
  }

  onChangeCategory(event: TslSelectChange): void {
    const categoryId = parseInt(event.value || 0, 0);
    this.filterSubcategories = JSON.parse(JSON.stringify(this.allSubcategories.filter(x => x.toolCategoryId === categoryId || categoryId === 0)));
    this.clearFormData();
  }

  onChangeSubcategory(event: TslSelectChange): void {
    if (this.toolCategory.value && event.value && event.value.length > 0) {
      this.defaultsGridData();
      if (!this.isOwner) {
        this.proposedDefaultsGridData();
      }
      this.manageGroup.controls['defaultType'].enable();
    } else this.manageGroup.controls['defaultType'].reset();
  }

  defaultsGridData() {
    this.deafultsData = [];
    if (this.defParameterList && this.defParameterList.length > 0) {
      let params = this.defParameterList.filter(x => this.toolSubcategory.value.includes(x.toolSubcategoryId));
      if (params && params.length > 0) {
        this.deafultsData = this.deafultsData.concat(params.map(x => {
          return {
            subCategoryId: x.toolSubcategoryId,
            paramId: x.id,
            name: x.type,
            type: 'Characteristic',
            eventType: null,
            subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory
          }
        }));
      }
    }
    if (this.defDeliverableList && this.defDeliverableList.length > 0) {
      let del = this.defDeliverableList.filter(x => this.toolSubcategory.value.includes(x.toolSubcategoryId));
      if (del && del.length > 0) {
        this.deafultsData = this.deafultsData.concat(del.map(x => {
          return {
            subCategoryId: x.toolSubcategoryId,
            deliverableId: x.id,
            name: x.deliverable,
            type: 'Deliverable',
            eventType: x.eventType,
            subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory
          }
        }));
      }
    }
    this.defaultsGrid();
  }

  defaultsGrid() {
    this.total = this.deafultsData.length;
    const skip = this.pageIndex * this.pageSize;
    const take = (this.pageIndex + 1) * this.pageSize;
    this.defaultDataSource.next(this.deafultsData.slice(skip, take));
  }

  onDefaultsPageChange(pageChange: PageEvent): void {
    const { previousPageIndex, pageIndex, pageSize } = pageChange;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.defaultsGrid();
  }

  clearFormData() {
    this.manageGroup.controls['defaultType'].reset();
    this.manageGroup.controls['parameter'].reset();
    this.manageGroup.controls['eventType'].reset();
    this.manageGroup.controls['deliverable'].reset();
    this.isParam = false;
    this.isDeliverable = false;
    this.parameterList = [];
    this.deafultsData = [];
    this.proposedDefaults = [];
    this.deliverableList = [];
  }

  onChangeDefaultType(event: TslSelectChange) {
    this.isParam = false;
    this.isDeliverable = false;
    if (event.value === 'Characteristic') {
      this.isParam = true;
      this.deliverableList = [];
      this.deliverable.setValue(null);
    } else if (event.value === 'Deliverable') {
      this.isDeliverable = true;
      // reset the controls and clear
      this.parameterList = [];
      this.parameter.setValue(null);
    }
  }


  addParameter(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add our parameter
    if ((value || '').trim() && this.parameter.valid) {
      if (this.parameterList && this.parameterList.length > 0 &&
        this.parameterList.filter(x => x.type === value) &&
        this.parameterList.filter(x => x.type === value).length > 0) {
        this.dialog.alert(this.translate.get("Alert"), value + this.translate.get("already exists!"));
      }
      else {
        this.parameterList.push(
          {
            id: 0,
            toolSubcategoryId: this.manageGroup.value.toolSubcategory,
            type: value.trim(),
            supplierCode: null
          });
      }
    }
    else if (!this.parameter.valid) {
      this.dialog.alert(this.translate.get("Alert"), this.translate.get("Only alphabets and numbers are allowed. ") + value + this.translate.get(" is in invalid format!"));
    }
    // Reset the input value
    if (input) {
      input.value = '';
      this.searchParameterList = [];
    }
    this.parameter.setValue(null);
  }

  onChangeEventType(event: TslSelectChange) {
  }

  addDeliverable(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    // Add our Deliverable
    if ((value || '').trim() && this.deliverable.valid) {
      if (this.deliverableList && this.deliverableList.length > 0 &&
        this.deliverableList.filter(x => x.deliverable === value && x.eventType === this.eventType.value) &&
        this.deliverableList.filter(x => x.deliverable === value && x.eventType === this.eventType.value).length > 0) {
        this.dialog.alert(this.translate.get("Alert"), value + this.translate.get(" already exists!"));
      }
      else {
        this.deliverableList.push(
          {
            id: 0,
            toolSubcategoryId: this.manageGroup.value.toolSubcategory,
            eventType: this.manageGroup.get('eventType').value,
            deliverable: value.trim()
          });
      }
    }
    else if (!this.deliverable.valid) {
      this.dialog.alert(this.translate.get("Alert"), this.translate.get("Only alphabets and numbers are allowed. ") + value + this.translate.get(" is in invalid format!"));
    }
    // Reset the input value
    if (input) {
      input.value = '';
      this.searchDeliverableList = [];
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getParametersList(searchText: string) {
    this.service.getParameterList(searchText).subscribe(res => {
      this.searchParameterList = res;
    });
  }

  getDeliverablesList(searchText: string) {
    this.service.getDeliverablesList(searchText).subscribe(res => {
      this.searchDeliverableList = res;
    });
  }

  mapSubCategoryDeliverables(defaults: any[]) {
    var mappedList = [];
    if (defaults && defaults.length > 0) {
      defaults.forEach(d => {
        d.toolSubcategoryId = this.toolSubcategory.value;
        if (d.toolSubcategoryId && d.toolSubcategoryId.length > 0) {
          d.toolSubcategoryId.forEach((sc: number) => {
            var item = new ToolDeliverableType;
            item.id = d.id;
            item.toolSubcategoryId = sc;
            item.eventType = this.eventType.value;
            item.deliverable = d.deliverable;
            mappedList.push(item);
          });
        }
      });
    }
    return mappedList;
  }

  // non-owners action
  proposeAsDefault(isDefault: boolean, status: string) {
    // save default parameters
    if (this.parameterList && this.parameterList.filter(i => i.id == 0).length > 0) {
      var defaultParameters = this.parameterList.filter(i => i.id == 0);
      defaultParameters = this.mapSubCategoryDefaults(defaultParameters);
      defaultParameters.forEach(p => {
        p.isDefault = isDefault;
        p.status = status;
      });
      if (this.checkForDuplicateDefaults('Parameter', defaultParameters)) {
        this.dialog.alert(this.translate.get('Duplicate Item'), this.translate.get('You are trying to set defaults that are already set/proposed as defaults. Please check and re-submit.'));
      }
      else {
        this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to submit your changes?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
          if (x) {
            this.service.createDefaultParamaters(defaultParameters).subscribe(x => {
              if (x && x.length > 0) {
                this.parameterList = [];
                this.snackBar.open(this.translate.get(`Successfully Submitted! Once approved by owners defaults will be added globally.`), null, {
                  duration: 5000,
                  verticalPosition: 'top',
                  panelClass: ['blue-snackbar']
                });
                // Get all the params list for that
                this.service.getPendingDefaultParameters().subscribe(res => {
                  this.proposedParamsList = [];
                  if (res && res.length > 0) {
                    this.proposedParamsList.push(...res);
                    this.proposedDefaultsGridData();
                  }
                });
              }
            });
          }
        });
      }
    }

    // save default deliverables
    if (this.deliverableList && this.deliverableList.filter(i => i.id == 0).length > 0) {
      var defaultDeliverables = this.deliverableList.filter(i => i.id == 0);
      defaultDeliverables = this.mapSubCategoryDeliverables(defaultDeliverables);
      defaultDeliverables.forEach(p => {
        p.isDefault = isDefault;
        p.status = status;
      });
      if (this.checkForDuplicateDefaults('Deliverable', defaultDeliverables)) {
        this.dialog.alert(this.translate.get('Duplicate Item'), this.translate.get('You are trying to set defaults that are already set/proposed as defaults. Please check and re-submit.'));
      }
      else if(this.checkForSimilarDefaults('Deliverable', defaultDeliverables)){
        var alertText = 'A deliverable with the same name and selected category and sub-category already exists under a different event type. Are you sure you would like to propose another deliverable with the same name under a new event type?';
        this.finishProposingDefualt(defaultDeliverables, alertText);
      }
      else {
        var alertText = 'Are you sure you want to submit your changes?';
        this.finishProposingDefualt(defaultDeliverables, alertText);
      }
    }
  }

  finishProposingDefualt(defaultDeliverables: any[], alertText: string){
    this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get(alertText), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
      if (x) {
        this.service.createDefaultDeliverables(defaultDeliverables).subscribe(x => {
          if (x && x.length > 0) {
            this.deliverableList = [];
            this.proposedDeliverableList = [];
            this.snackBar.open(this.translate.get(`Successfully Submitted! Once approved defaults will be added globally.`), null, {
              duration: 5000,
              verticalPosition: 'top',
              panelClass: ['blue-snackbar']
            });
            // Get all the deliverables list for that tool sub category
            this.service.getPendingDefaultDeliverables().subscribe(res => {
              if (res && res.length > 0) {
                this.proposedDeliverableList.push(...res);
                this.proposedDefaultsGridData();
              }
            });
          }
        });
      }
    });
  }

  proposedDefaultsGridData() {
    this.proposedDefaults = [];
    if (this.toolSubcategory && this.toolSubcategory.value.length > 0) {
      if (this.proposedParamsList && this.proposedParamsList.length > 0) {
        let params = this.proposedParamsList.filter(x => this.toolSubcategory.value.includes(x.toolSubcategoryId));
        if (params && params.length > 0) {
          this.proposedDefaults = this.proposedDefaults.concat(params.map(x => {
            return {
              subCategoryId: x.toolSubcategoryId,
              paramId: x.id,
              name: x.type,
              type: 'Characteristic',
              eventType: null,
              subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory
            }
          }));
        }
      }
      if (this.proposedDeliverableList && this.proposedDeliverableList.length > 0) {
        let del = this.proposedDeliverableList.filter(x => this.toolSubcategory.value.includes(x.toolSubcategoryId));
        if (del && del.length > 0) {
          this.proposedDefaults = this.proposedDefaults.concat(del.map(x => {
            return {
              subCategoryId: x.toolSubcategoryId,
              deliverableId: x.id,
              name: x.deliverable,
              type: 'Deliverable',
              eventType: x.eventType,
              subCategory: this.allSubcategories.filter(sc => sc.id == x.toolSubcategoryId)[0].subcategory
            }
          }));
        }
      }
    }
    this.proposedDefaultsGrid();
  }

  proposedDefaultsGrid() {
    this.length = this.proposedDefaults.length;
    const skip = this.pIndex * this.pSize;
    const take = (this.pIndex + 1) * this.pSize;
    this.dataSource.next(this.proposedDefaults.slice(skip, take));
  }

  onProposedDefaultsPageChange(pageChange: PageEvent): void {
    const { pageIndex, pageSize } = pageChange;
    this.pIndex = pageIndex;
    this.pSize = pageSize;
    this.proposedDefaultsGrid();
  }

  setAsDefault(isDefault: boolean, status: string) {
    // save default parameters
    if (this.parameterList && this.parameterList.filter(i => i.id == 0).length > 0) {
      var defaultParameters = this.parameterList.filter(i => i.id == 0);
      defaultParameters = this.mapSubCategoryDefaults(defaultParameters);
      defaultParameters.forEach(p => {
        p.isDefault = isDefault;
        p.status = status;
      });
      if (this.checkForDuplicateDefaults('Parameter', defaultParameters)) {
        this.dialog.alert(this.translate.get('Duplicate Item'), this.translate.get('You are trying to set defaults that are already set/proposed as defaults. Please check and re-submit.'));
      }
      else {
        this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to submit your changes?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
          if (x) {
            this.service.createDefaultParamaters(defaultParameters).subscribe(x => {
              if (x && x.length > 0) {
                this.parameterList = [];
                this.snackBar.open(this.translate.get(`Successfully set as defaults.`), null, {
                  duration: 5000,
                  verticalPosition: 'top',
                  panelClass: ['blue-snackbar']
                });
                // Get all the params list for that tool sub category
                if (this.toolSubcategory.value && this.toolSubcategory.value.length > 0) {
                  this.service.getDefaultParameters()
                    .subscribe(res => {
                      this.defParameterList = [];
                      this.defParameterList.push(...res);
                      this.defaultsGridData();
                    });
                }
              }
            });
          }
        });
      }
    }

    // save default deliverables
    if (this.deliverableList && this.deliverableList.filter(i => i.id == 0).length > 0) {
      var defaultDeliverables = this.deliverableList.filter(i => i.id == 0);
      defaultDeliverables = this.mapSubCategoryDeliverables(defaultDeliverables);
      defaultDeliverables.forEach(p => {
        p.isDefault = isDefault;
        p.status = status;
      });

      if (this.checkForDuplicateDefaults('Deliverable', defaultDeliverables)) {
        this.dialog.alert(this.translate.get('Duplicate Item'), this.translate.get('You are trying to set defaults that are already set/proposed as defaults. Please check and re-submit.'));
      }
      else if(this.checkForSimilarDefaults('Deliverable', defaultDeliverables)){
        var alertText = 'A deliverable with the same name and selected category and sub-category already exists under a different event type. Are you sure you would like to create another deliverable with the same name under a new event type?';
        this.finishSettingDefualt(defaultDeliverables, alertText);
      }
      else {
        var alertText = 'Are you sure you want to submit your changes?';
        this.finishSettingDefualt(defaultDeliverables, alertText);
      }
    }
  }

  finishSettingDefualt(defaultDeliverables: any[], alertText: string){
    this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get(alertText), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
      if (x) {
        this.service.createDefaultDeliverables(defaultDeliverables).subscribe(x => {
          if (x && x.length > 0) {
            this.snackBar.open(this.translate.get(`Successfully set as defaults.`), null, {
              duration: 5000,
              verticalPosition: 'top',
              panelClass: ['blue-snackbar']
            });
            // Get all the deliverables list for that tool sub category
            this.service.getDefaultDeliverables()
              .subscribe(res => {
                this.defDeliverableList = [];
                if (res && res.length > 0) {
                  this.defDeliverableList.push(...res);
                  this.defaultsGridData();
                }
              });
          }
        });
      }
    });
  }

  onDelete(selection: any) {
    if (selection.type === 'Parameter') {
      let item = new ToolParameterType;
      item.id = selection.paramId;
      item.supplierCode = null;
      item.toolSubcategoryId = selection.subCategoryId;
      item.type = selection.name;
      this.onRemoveParameter(item);
    }
    else if (selection.type === 'Deliverable') {
      let item = new ToolDeliverableType;
      item.id = selection.deliverableId;
      item.toolSubcategoryId = selection.subCategoryId;
      item.deliverable = selection.name;
      item.eventType = selection.eventType;
      this.onRemoveDeliverable(item);
    }
  }

  onRemoveParameter(item: ToolParameterType): void {
    item.isDefault = false;
    this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to remove this default?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
      if (x) {
        if (item.id > 0) {
          // Do not delete but disable from default
          this.service.disableDefaultParamaters(item).subscribe(x => {
            if (x) {
              this.defParameterList = this.defParameterList.filter(d => d.id != x.id);
              this.defaultsGridData();
              this.snackBar.open(this.translate.get(`Successfully removed as default.`), null, {
                duration: 5000,
                verticalPosition: 'top',
                panelClass: ['blue-snackbar']
              });
            }
          });
        }
        else if (item.id == 0) {
          const index = this.parameterList.indexOf(item);
          if (index >= 0) {
            this.parameterList.splice(index, 1);
          }
        }
      }
    });
  }

  onRemoveDeliverable(item: ToolDeliverableType): void {
    item.isDefault = false;
    this.dialog.confirm(this.translate.get('Confirm Submission'), this.translate.get('Are you sure you want to remove this default?'), this.translate.get('Yes'), this.translate.get('No')).afterClosed().subscribe((x: boolean) => {
      if (x) {
        if (item.id > 0) {
          // Do not delete but disable from default
          this.service.disableDefaultDeliverables(item).subscribe(x => {
            if (x) {
              this.defDeliverableList = this.defDeliverableList.filter(d => d.id != x.id);
              this.defaultsGridData();
              this.snackBar.open(this.translate.get(`Successfully removed as default.`), null, {
                duration: 5000,
                verticalPosition: 'top',
                panelClass: ['blue-snackbar']
              });
            }
          });
        }
        else if (item.id == 0) {
          const index = this.deliverableList.indexOf(item);
          if (index >= 0) {
            this.deliverableList.splice(index, 1);
          }
        }
      }
    });
  }

  onSelectFromSearchList(option: any, type: string) {
    if (option && option.value && type) {
      var index = -1;
      if (type === 'parameter') {
        var item = {
          id: 0,
          toolSubcategoryId: this.manageGroup.value.toolSubcategory,
          type: option.value.trim(),
          supplierCode: null
        };

        if (this.parameterList && this.parameterList.length > 0 &&
          this.parameterList.filter(x => x.type === option.value) &&
          this.parameterList.filter(x => x.type === option.value).length > 0) {
          this.dialog.alert(this.translate.get("Alert"), option.value + this.translate.get(" already exists!"));
        }
        else {
          if (this.parameterList && this.parameterList.length > 0) {
            // replace the search text from param list
            index = this.parameterList.findIndex(x => x.type == this.parameter.value);
          }
          // Add our parameter list
          this.pushToArray(this.parameterList, index, item);
        }
        this.parameter.setValue(null);
        this.searchParameterList = [];
      }
      else if (type === 'deliverable') {
        var row =
        {
          id: 0,
          toolSubcategoryId: this.manageGroup.value.toolSubcategory,
          eventType: this.eventType.value,
          deliverable: option.value.trim()
        };
        // Add our Deliverable
        if ((option.value || '').trim()) {
          if (this.deliverableList && this.deliverableList.length > 0 &&
            this.deliverableList.filter(x => x.deliverable === option.value && x.eventType === this.eventType.value) &&
            this.deliverableList.filter(x => x.deliverable === option.value && x.eventType === this.eventType.value).length > 0) {
            this.dialog.alert(this.translate.get("Alert"), option.value + this.translate.get(" already exists!"));
          }
          else {
            if (this.deliverableList && this.deliverableList.length > 0) {
              // replace the search text from list
              var index = this.deliverableList.findIndex(x => x.deliverable == this.deliverable.value);
            }
            // Add to the list
            this.pushToArray(this.deliverableList, index, row);
          }
          this.deliverable.setValue(null);
          this.searchParameterList = [];
        }
      }
    }
  }

  pushToArray(arr: any, index: number, item: any) {
    if (index === -1) {
      arr.push(item);
    } else if (index > -1) {
      arr[index] = item;
    }
  }

  mapSubCategoryDefaults(defaults: any[]) {
    var mappedList = [];
    if (defaults && defaults.length > 0) {
      defaults.forEach(d => {
        d.toolSubcategoryId = this.toolSubcategory.value;
        if (d.toolSubcategoryId && d.toolSubcategoryId.length > 0) {
          d.toolSubcategoryId.forEach((sc: number) => {
            var item = new ToolParameterType;
            item.id = d.id;
            item.toolSubcategoryId = sc;
            item.type = d.type;
            item.supplierCode = null;
            mappedList.push(item);
          });
        }
      });
    }
    return mappedList;
  }


  checkForDuplicateDefaults(type: string, data: any[]) {
    var alreadyExists = false;
    if (type === 'Parameter') {
      // check for duplicates
      if (this.defParameterList && this.defParameterList.length > 0) {
        data.forEach(x => {
          if (this.defParameterList.some(d => d.toolSubcategoryId === x.toolSubcategoryId && d.type === x.type)) {
            alreadyExists = true;
          }
        });
      }
      if (this.proposedParamsList && this.proposedParamsList.length > 0) {
        data.forEach(x => {
          if (this.proposedParamsList.some(d => d.toolSubcategoryId === x.toolSubcategoryId && d.type === x.type)) {
            alreadyExists = true;
          }
        });
      }
    }
    if (type === 'Deliverable') {
      if (this.defDeliverableList && this.defDeliverableList.length > 0) {
        data.forEach(x => {
          if (this.defDeliverableList.some(d => d.toolSubcategoryId === x.toolSubcategoryId &&
            d.deliverable === x.deliverable && d.eventType === x.eventType)) {
            alreadyExists = true;
          }
        });
      }
      if (this.proposedDeliverableList && this.proposedDeliverableList.length > 0) {
        data.forEach(x => {
          if (this.proposedDeliverableList.some(d => d.toolSubcategoryId === x.toolSubcategoryId &&
            d.deliverable === x.deliverable && d.eventType === x.eventType)) {
            alreadyExists = true;
          }
        });
      }
    }
    return alreadyExists;
  }

  checkForSimilarDefaults(type: string, data: any[]) {
    var similarExists = false;
    if (type === 'Deliverable') {
      if (this.defDeliverableList && this.defDeliverableList.length > 0) {
        data.forEach(x => {
          if (this.defDeliverableList.some(d => d.toolSubcategoryId === x.toolSubcategoryId &&
            d.deliverable === x.deliverable)) {
              similarExists = true;
          }
        });
      }
      if (this.proposedDeliverableList && this.proposedDeliverableList.length > 0) {
        data.forEach(x => {
          if (this.proposedDeliverableList.some(d => d.toolSubcategoryId === x.toolSubcategoryId &&
            d.deliverable === x.deliverable)) {
              similarExists = true;
          }
        });
      }
    }
    return similarExists;
  }
}